import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'
import { Database, ArrowLeft, Shield, Clock } from 'lucide-react'
import { useState, useEffect } from 'react'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/screenata/evidence',
)({
  component: EvidenceVaultPage,
})

type FreshnessFilter = 'all' | 'fresh' | 'stale' | 'expired'
type SourceTypeFilter = 'all' | string

type EvidenceItem = {
  id: string
  controlRef: string
  policyName: string | null
  sourceType: string
  title: string
  description: string | null
  freshnessState: string
  collectedAt: Date | string
  expiresAt: Date | string
  confidence: string
  collectedBy: string
}

function FreshnessBadge({ state }: { state: string }) {
  if (state === 'fresh') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Fresh
      </span>
    )
  }
  if (state === 'stale') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Stale
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
      Expired
    </span>
  )
}

function SourceTypeBadge({ sourceType }: { sourceType: string }) {
  const label = sourceType
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
      {label}
    </span>
  )
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  if (confidence === 'high') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
        High confidence
      </span>
    )
  }
  if (confidence === 'medium') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
        Medium confidence
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
      Low confidence
    </span>
  )
}

const SCAN_SOURCE_TYPES = new Set(['cloud_scan', 'repo_scan', 'code_scan'])

function VerificationLabel({ collectedBy }: { collectedBy: string }) {
  const isApiVerified = SCAN_SOURCE_TYPES.has(collectedBy)
  if (isApiVerified) {
    return <span className="text-xs text-emerald-700 font-medium">API-verified</span>
  }
  return <span className="text-xs text-gray-500">Manual upload</span>
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const SOURCE_TYPE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'cloud_scan', label: 'Cloud Scan' },
  { value: 'repo_scan', label: 'Repo Scan' },
  { value: 'code_scan', label: 'Code Scan' },
  { value: 'pr_review', label: 'PR Review' },
  { value: 'policy_attestation', label: 'Policy Attestation' },
  { value: 'manual_upload', label: 'Manual Upload' },
]

function EvidenceVaultPage() {
  const { workspaceId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/screenata/evidence',
  })

  const [programId, setProgramId] = useState<string | null>(null)
  const [freshnessFilter, setFreshnessFilter] = useState<FreshnessFilter>('all')
  const [sourceTypeFilter, setSourceTypeFilter] = useState<SourceTypeFilter>('all')

  // Get program ID first
  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' }),
  )

  useEffect(() => {
    if (programQuery.data?.id) {
      setProgramId(programQuery.data.id)
    }
  }, [programQuery.data])

  // Get evidence summary
  const summaryQuery = useQuery({
    ...trpc.complianceProgram.getEvidenceSummary.queryOptions({ programId: programId! }),
    enabled: !!programId,
  })

  // Get evidence items with filters applied — returns Record<controlRef, EvidenceItem[]>
  const evidenceQuery = useQuery({
    ...trpc.complianceProgram.getEvidence.queryOptions({
      programId: programId!,
      ...(freshnessFilter !== 'all' ? { freshnessState: freshnessFilter } : {}),
      ...(sourceTypeFilter !== 'all' ? { sourceType: sourceTypeFilter } : {}),
    }),
    enabled: !!programId,
  })

  const summary = summaryQuery.data
  // evidenceQuery.data is Record<controlRef, EvidenceItem[]>
  const grouped = (evidenceQuery.data ?? {}) as Record<string, EvidenceItem[]>
  const groupKeys = Object.keys(grouped).sort()
  const totalItems = groupKeys.reduce((sum, key) => sum + grouped[key].length, 0)

  if (!programId && !programQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No compliance program found. Start from the chat page.
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/workspaces/$workspaceId/screenata"
          params={{ workspaceId }}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <Database className="w-8 h-8 text-emerald-600" />
        <div>
          <h1 className="text-2xl font-semibold">Evidence Vault</h1>
          <p className="text-muted-foreground">
            Collected evidence items with freshness tracking
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="border rounded-lg p-4 text-center">
          <div className="text-3xl font-bold">{summary?.total ?? 0}</div>
          <p className="text-sm text-muted-foreground mt-1">Total Evidence</p>
        </div>
        <div className="border rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-emerald-600">
            {summary?.byFreshness.fresh ?? 0}
          </div>
          <p className="text-sm text-muted-foreground mt-1">Fresh</p>
        </div>
        <div className="border rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-amber-500">
            {summary?.byFreshness.stale ?? 0}
          </div>
          <p className="text-sm text-muted-foreground mt-1">Stale</p>
        </div>
        <div className="border rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-red-600">
            {summary?.byFreshness.expired ?? 0}
          </div>
          <p className="text-sm text-muted-foreground mt-1">Expired</p>
        </div>
      </div>

      {/* Freshness progress bar */}
      {summary && summary.total > 0 && (
        <div>
          <div className="flex justify-between items-center mb-1.5 text-xs text-muted-foreground">
            <span>Evidence freshness</span>
            <span className="text-emerald-700 font-medium">
              {Math.round(summary.freshPercent)}% fresh
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 flex overflow-hidden">
            <div
              className="bg-emerald-500 h-2 transition-all"
              style={{ width: `${(summary.byFreshness.fresh / summary.total) * 100}%` }}
            />
            <div
              className="bg-amber-400 h-2 transition-all"
              style={{ width: `${(summary.byFreshness.stale / summary.total) * 100}%` }}
            />
            <div
              className="bg-red-500 h-2 transition-all"
              style={{ width: `${(summary.byFreshness.expired / summary.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Filter Row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <Shield className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Freshness:</span>
        </div>
        {(['all', 'fresh', 'stale', 'expired'] as FreshnessFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFreshnessFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              freshnessFilter === f
                ? f === 'all'
                  ? 'bg-gray-800 text-white'
                  : f === 'fresh'
                    ? 'bg-emerald-600 text-white'
                    : f === 'stale'
                      ? 'bg-amber-500 text-white'
                      : 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Source:</span>
        </div>
        <select
          value={sourceTypeFilter}
          onChange={(e) => setSourceTypeFilter(e.target.value)}
          className="text-xs border rounded-md px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          {SOURCE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Evidence List */}
      {evidenceQuery.isLoading || (programId && summaryQuery.isLoading) ? (
        <div className="text-center text-muted-foreground py-12">
          Loading evidence...
        </div>
      ) : totalItems === 0 ? (
        <div className="text-center py-16 border rounded-xl">
          <Database className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-muted-foreground">
            {freshnessFilter !== 'all' || sourceTypeFilter !== 'all'
              ? 'No evidence items match the selected filters.'
              : 'No evidence collected yet. Run a scan to populate the vault.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupKeys.map((controlRef) => (
            <div key={controlRef}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded font-medium">
                  {controlRef}
                </span>
                <span className="text-xs text-muted-foreground">
                  {grouped[controlRef].length} item{grouped[controlRef].length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="space-y-2">
                {grouped[controlRef].map((item) => (
                  <div
                    key={item.id}
                    className="border rounded-lg p-4 hover:border-emerald-300 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-sm">{item.title}</h3>
                          <FreshnessBadge state={item.freshnessState} />
                          <SourceTypeBadge sourceType={item.sourceType} />
                          <ConfidenceBadge confidence={item.confidence} />
                        </div>
                        {item.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 pt-2.5 border-t flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-3">
                        <span>Collected {formatDate(item.collectedAt)}</span>
                        <span>Expires {formatDate(item.expiresAt)}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {item.policyName && (
                          <span className="text-gray-500">Policy: {item.policyName}</span>
                        )}
                        <VerificationLabel collectedBy={item.collectedBy} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
